import { BUSINESS_KIND_LIST_LABELS, IMPORT_STATUS_LABELS, type ImportJobDTO, type LeadListResponse } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { FolderSearch, Mail, Store } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Page } from '../../../components/layout/Page';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, PageSpinner, Spinner } from '../../../components/ui';
import { useAuth } from '../../../hooks/useAuth';
import { api, apiUrl } from '../../../lib/api';
import { formatDateTime, formatNumber, pluralize } from '../../../lib/format';
import { IMPORT_STATUS_COLORS } from '../ImportsPage';
import type { NotImported } from './ScanPage';
import { batchSummary } from './scanImport';
import { KIND_LIST_PATHS, kindCount } from '../../leads/model';

/** Every import of one folder scan: what each file brought in and what still needs a person. */
export function BatchPage() {
  const { batchId = '' } = useParams();
  const location = useLocation();
  const notImported = (location.state as { notImported?: NotImported } | null)?.notImported ?? [];
  const navigate = useNavigate();
  const { can } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['imports', 'list', { batchId }],
    queryFn: ({ signal }) => api.get<{ items: ImportJobDTO[] }>(apiUrl('/api/imports', { batchId }), { signal }),
    refetchInterval: (query) => (query.state.data?.items.some((job) => job.status === 'validating' || job.status === 'importing') ? 1_500 : false),
  });
  const jobs = data?.items ?? [];
  const summary = batchSummary(jobs);
  const handOff = summary.imported > 0 && summary.running === 0;
  // A folder scan gives every file the same kind; a batch whose files were later switched to different kinds has no
  // single list, so its hand-offs fall back to Leads.
  const kind = jobs.length > 0 && jobs.every((job) => job.businessKind === jobs[0]!.businessKind) ? jobs[0]!.businessKind : 'pizzeria';
  // The batch's leads as its list counts them (created + merged, still visible, of that kind), for the hand-offs.
  const { data: batchLeads } = useQuery({
    queryKey: ['leads', 'list', { importBatchId: batchId, kinds: [kind], pageSize: 1 }],
    queryFn: ({ signal }) => api.get<LeadListResponse>(apiUrl('/api/leads', { importBatchId: batchId, kinds: [kind], pageSize: 1 }), { signal }),
    enabled: handOff,
  });

  if (isLoading) return <PageSpinner />;
  const breadcrumbs = [{ label: 'Imports', to: '/imports' }, { label: 'Folder import' }];
  if (jobs.length === 0) {
    return (
      <Page width="wide">
        <PageHeader title="Folder import" breadcrumbs={breadcrumbs} />
        <Card>
          <EmptyState
            icon={FolderSearch}
            title="No files in this folder import"
            description="It may belong to someone else, or no file could be uploaded."
            action={<Button onClick={() => navigate('/imports/scan')}>Scan a folder</Button>}
          />
        </Card>
      </Page>
    );
  }

  const first = jobs[0]!;
  const leadCount = batchLeads?.total ?? jobs.reduce((sum, job) => sum + job.createdCount + job.mergedCount, 0);
  const leadsUrl = `${KIND_LIST_PATHS[kind]}?importBatchId=${batchId}`;
  const plural = BUSINESS_KIND_LIST_LABELS[kind].toLowerCase();
  const importing = jobs.filter((job) => job.status === 'importing').length;
  const parts = [
    pluralize(summary.files, 'file'),
    summary.undecided > 0 && `${pluralize(summary.undecided, 'possible duplicate')} to decide`,
    summary.needsMatching > 0 && `${pluralize(summary.needsMatching, 'file')} ${summary.needsMatching === 1 ? 'needs' : 'need'} column matching`,
    summary.running - importing > 0 && `${pluralize(summary.running - importing, 'file')} being checked`,
    importing > 0 && `${pluralize(importing, 'file')} importing`,
    summary.imported > 0 && `${pluralize(summary.imported, 'file')} imported`,
  ].filter(Boolean);
  const noConsent = jobs.every((job) => (job.status !== 'validated' && job.status !== 'completed') || job.defaultConsentBasis === 'none');
  // Nothing waits for a person any more: every file imported with no decision left, failed or cancelled.
  const settled = jobs.every((job) => job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled');

  return (
    <Page width="wide">
      <PageHeader
        title="Folder import"
        breadcrumbs={breadcrumbs}
        description={`Files picked in a folder scan · ${formatDateTime(first.createdAt)} by ${first.createdBy?.name ?? 'someone'}`}
      />

      <Card className="mb-4">
        <CardBody className="space-y-3 text-[13px]">
          <p className="font-medium text-slate-900">{parts.join(' · ')}</p>
          {(summary.undecided > 0 || summary.needsMatching > 0) && (
            <p className="text-slate-600">Files with possible duplicates to decide, or whose columns need matching, wait for you: open them below. Everything else is already in your leads.</p>
          )}
          {handOff && leadCount > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button icon={Store} onClick={() => navigate(leadsUrl)}>
                See these {kindCount(kind, leadCount)}
              </Button>
              {can('email:bulk') && (
                <Button
                  variant={settled ? 'primary' : 'secondary'}
                  icon={Mail}
                  onClick={() =>
                    navigate('/email/bulk', {
                      state: {
                        selection: { mode: 'filter', filter: { importBatchId: batchId, kinds: [kind] }, excludeIds: [] },
                        count: leadCount,
                        description: `${BUSINESS_KIND_LIST_LABELS[kind]} from a folder import (${pluralize(summary.files, 'file')})`,
                      },
                    })
                  }
                >
                  Email these {plural}
                </Button>
              )}
            </div>
          )}
          {handOff && leadCount > 0 && noConsent && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              No email consent was recorded with these files, so bulk email skips these leads under Canada’s anti-spam law (CASL) until consent is recorded. Open{' '}
              <Link to={leadsUrl} className="font-medium underline">
                these {plural}
              </Link>
              , select them and choose <strong>Record email consent</strong> if you know where the addresses came from.
            </p>
          )}
          <p className="text-xs text-slate-500">
            Leads that appear in more than one of these files are flagged on the{' '}
            <Link to="/duplicates" className="font-medium text-slate-700 underline">
              Duplicates
            </Link>{' '}
            page after importing: each file is compared with your CRM when its rows are checked, before the other files are imported.
          </p>
        </CardBody>
      </Card>

      {notImported.length > 0 && (
        <Card className="mb-4 border-amber-200">
          <CardHeader title={`${pluralize(notImported.length, 'file')} could not be uploaded`} description="They are not part of this folder import." />
          <ul className="divide-y divide-slate-100 text-[13px]">
            {notImported.map((file) => (
              <li key={file.path} className="px-4 py-2">
                <span className="font-medium text-slate-800">{file.path}</span> <span className="text-slate-600">— {file.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <CardHeader title="Files" description="Open a file to match its columns, decide its possible duplicates or look through its rows." />
        <ul className="divide-y divide-slate-100">
          {jobs.map((job) => (
            <BatchFile key={job.id} job={job} />
          ))}
        </ul>
      </Card>
    </Page>
  );
}

function BatchFile({ job }: { job: ImportJobDTO }) {
  const notImported = job.skippedCount + job.failedCount + job.invalidRows;
  const link = `/imports/${job.id}`;
  let detail: ReactNode;
  let action: ReactNode = null;
  switch (job.status) {
    case 'uploaded':
      detail = `${pluralize(job.totalRows, 'row')} · no column was taken for the business name`;
      action = <ActionLink to={link}>Match columns</ActionLink>;
      break;
    case 'validating':
    case 'importing':
      detail = (
        <span className="inline-flex items-center gap-1.5">
          <Spinner className="size-3.5" />
          {job.status === 'validating' ? `Checking ${pluralize(job.totalRows, 'row')}…` : 'Importing…'}
        </span>
      );
      break;
    case 'validated':
    case 'completed':
      detail = [
        `${formatNumber(job.createdCount)} created`,
        `${formatNumber(job.mergedCount)} merged`,
        job.junkRows > 0 && `${formatNumber(job.junkRows)} set aside as junk`,
        notImported > 0 && `${formatNumber(notImported)} not imported`,
      ]
        .filter(Boolean)
        .join(' · ');
      if (job.unresolvedDuplicates > 0) action = <ActionLink to={link}>Decide {pluralize(job.unresolvedDuplicates, 'duplicate')}</ActionLink>;
      break;
    case 'failed':
      detail = <span className="text-red-700">Failed: {job.error ?? 'unknown error'}</span>;
      break;
    case 'cancelled':
      detail = 'Cancelled — nothing imported';
      break;
  }
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-[13px]">
      <Link to={link} className="min-w-0 truncate font-medium text-slate-900 hover:text-ember-700">
        {job.filename}
      </Link>
      <Badge color={IMPORT_STATUS_COLORS[job.status]} size="sm">
        {IMPORT_STATUS_LABELS[job.status]}
      </Badge>
      <span className="basis-full text-slate-600 sm:ml-auto sm:basis-auto">{detail}</span>
      {action}
    </li>
  );
}

function ActionLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="font-medium text-ember-700 hover:underline">
      {children} →
    </Link>
  );
}
